import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';

interface CaseWithSlots {
  id: number;
  case_name: string;
  case_type: string;
  status: string;
  creator: {
    name: string;
    email: string;
  };
  firm: {
    name: string;
  };
  service_type: {
    name: string;
    short_code: string;
  };
  total_slots: number;
  available_slots: number;
  booked_slots: number;
  slots: Array<{
    id: number;
    available_date: string;
    time_slot: string;
    is_booked: boolean;
    booked_by?: {
      name: string;
      email: string;
    };
    booked_at?: string;
    created_at: string;
  }>;
}

const CasesWithSlots: React.FC = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState<CaseWithSlots[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCasesWithSlots();
  }, []);

  const fetchCasesWithSlots = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/cases-with-slots');
      setCases(response.data.cases);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to fetch cases with slots'
      );
      toast.error('Failed to fetch cases with slots');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Virtual_Meeting_Requested':
        return 'warning';
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          onClick={() => navigate('/admin/cases')}
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2 }}
        >
          Back to Cases
        </Button>
        <Typography variant='h4' sx={{ flexGrow: 1 }}>
          Cases with Meeting Slots
        </Typography>
        <Button
          variant='contained'
          onClick={fetchCasesWithSlots}
          startIcon={<CalendarIcon />}
        >
          Refresh
        </Button>
      </Box>

      {/* Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            Summary
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant='body2' color='text.secondary'>
                Total Cases with Slots
              </Typography>
              <Typography variant='h4' color='primary'>
                {cases.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant='body2' color='text.secondary'>
                Total Slots
              </Typography>
              <Typography variant='h4' color='primary'>
                {cases.reduce((sum, caseItem) => sum + caseItem.total_slots, 0)}
              </Typography>
            </Box>
            <Box>
              <Typography variant='body2' color='text.secondary'>
                Available Slots
              </Typography>
              <Typography variant='h4' color='success.main'>
                {cases.reduce(
                  (sum, caseItem) => sum + caseItem.available_slots,
                  0
                )}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Cases List */}
      {cases.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign='center' py={4}>
              <CalendarIcon
                sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant='h6' color='text.secondary' gutterBottom>
                No Cases with Slots Found
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Cases will appear here once meeting slots are created from Azure
                Calendar.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        cases.map(caseItem => (
          <Accordion key={caseItem.id} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant='h6'>{caseItem.case_name}</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {caseItem.firm?.name} • {caseItem.creator?.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={caseItem.status.replace(/_/g, ' ')}
                    color={getStatusColor(caseItem.status) as any}
                    size='small'
                    onClick={() => false}
                  />
                  <Chip
                    icon={<CalendarIcon />}
                    label={`${caseItem.total_slots} slots`}
                    variant='outlined'
                    size='small'
                    onClick={() => false}
                  />
                  <Chip
                    icon={<CheckIcon />}
                    label={`${caseItem.available_slots} available`}
                    color='success'
                    size='small'
                    onClick={() => false}
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 2 }}>
                <Typography variant='subtitle2' gutterBottom>
                  Case Details
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant='body2' color='text.secondary'>
                      <strong>Case Type:</strong> {caseItem.case_type}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      <strong>Service Type:</strong>{' '}
                      {caseItem.service_type?.name || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='body2' color='text.secondary'>
                      <strong>Firm Admin:</strong> {caseItem.creator?.name}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      <strong>Email:</strong> {caseItem.creator?.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Typography variant='subtitle2' gutterBottom>
                Meeting Slots ({caseItem.total_slots} total)
              </Typography>

              <TableContainer component={Paper} sx={{ mt: 1 }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Booked By</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {caseItem.slots.map(slot => (
                      <TableRow key={slot.id}>
                        <TableCell>{formatDate(slot.available_date)}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <TimeIcon fontSize='small' color='action' />
                            {slot.time_slot}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={
                              slot.is_booked ? <CancelIcon /> : <CheckIcon />
                            }
                            label={slot.is_booked ? 'Booked' : 'Available'}
                            color={slot.is_booked ? 'error' : 'success'}
                            size='small'
                            onClick={() => false}
                          />
                        </TableCell>
                        <TableCell>
                          {slot.is_booked && slot.booked_by ? (
                            <Tooltip title={slot.booked_by.email}>
                              <Typography variant='body2'>
                                {slot.booked_by.name}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant='body2' color='text.secondary'>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2' color='text.secondary'>
                            {formatDate(slot.created_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default CasesWithSlots;
